import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "../../../lib/utils";
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "../../../components/ui/dialog";
import { inventoryManagementService } from "../../../services/inventoryManagementService";

const MONTH_KEYS = [
  "dateTime.monthJan",
  "dateTime.monthFeb",
  "dateTime.monthMar",
  "dateTime.monthApr",
  "dateTime.monthMay",
  "dateTime.monthJun",
  "dateTime.monthJul",
  "dateTime.monthAug",
  "dateTime.monthSep",
  "dateTime.monthOct",
  "dateTime.monthNov",
  "dateTime.monthDec",
];

const SOURCE_LABEL_KEYS = {
  Purchase_From_Vendor: "inventory.purchaseFromVendor",
  Warranty_Replacement_From_Vendor: "inventory.warrantyReplacementFromVendor",
  Defective_Replacement_From_Vendor: "inventory.defectiveReplacementFromVendor",
  Excess_Return_From_Customer: "inventory.excessReturnFromCustomer",
  Warranty_Replacement_From_Customer: "inventory.warrantyReplacementFromCustomer",
  Defective_Replacement_From_Customer:
    "inventory.defectiveReplacementFromCustomer",
};

function formatDate(isoStr, t) {
  if (!isoStr) return "—";
  const dateObj = new Date(isoStr);
  if (!Number.isFinite(dateObj.getTime())) return "—";
  const day = String(dateObj.getDate()).padStart(2, "0");
  const month = t(MONTH_KEYS[dateObj.getMonth()]);
  const year = dateObj.getFullYear();
  return `${day} ${month} ${year}`;
}

function formatMoney(val) {
  if (val == null || val === "") return "—";
  const n = Number(val);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

function formatQty(val) {
  if (val == null || val === "") return "—";
  const n = Number(val);
  return Number.isFinite(n) ? n.toLocaleString() : "—";
}

/**
 * @param {{
 *   open: boolean,
 *   onOpenChange: (open: boolean) => void,
 *   inventoryInId?: string | number | null,
 * }} props
 */
export function InventoryRecordDetailModal({
  open,
  onOpenChange,
  inventoryInId = null,
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [record, setRecord] = useState(null);

  useEffect(() => {
    if (!open || inventoryInId == null || String(inventoryInId).trim() === "") {
      setRecord(null);
      setError("");
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);
    setError("");
    setRecord(null);

    inventoryManagementService
      .getInventoryRecordById(inventoryInId)
      .then(async (detail) => {
        if (!active) return;
        const [enriched] =
          await inventoryManagementService.enrichInventoryRecordCustomerNames([
            {
              ...detail,
              partyName: detail.customerName || detail.vendorName || "",
              customerId: detail.customerId,
              sourceType: detail.sourceType,
            },
          ]);
        if (!active) return;
        setRecord({
          ...detail,
          customerName: enriched?.customerName || detail.customerName,
          partyName:
            enriched?.partyName ||
            detail.vendorName ||
            detail.customerName ||
            "—",
        });
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        console.error("Failed to load inventory record detail:", err);
        setError(t("validation.failedToLoadInventoryRecord"));
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, inventoryInId, t]);

  const sourceLabel = (sourceType) => {
    const key = SOURCE_LABEL_KEYS[sourceType];
    return key ? t(key) : sourceType || "—";
  };

  const rows = record
    ? [
        {
          label: t("inventory.reference"),
          value: record.inventoryInId || "—",
        },
        {
          label: t("inventory.sourceType"),
          value: sourceLabel(record.sourceType),
        },
        {
          label: t("inventory.product"),
          value: record.productName || record.productId || "—",
        },
        {
          label: t("inventory.batchNumber"),
          value: record.batchNumber || "—",
        },
        {
          label: t("dateTime.receiptDate"),
          value: formatDate(record.receiptDate, t),
        },
        {
          label: t("inventory.warehouseLocation"),
          value: record.warehouseName || record.warehouseId || "—",
        },
        {
          label: t("inventory.party"),
          value:
            record.partyName ||
            record.vendorName ||
            record.customerName ||
            "—",
        },
        {
          label: t("inventory.receivedQuantity"),
          value: formatQty(record.receivedQty),
        },
        {
          label: t("inventory.defectiveQuantity"),
          value: formatQty(record.defectiveQty),
        },
        {
          label: t("inventory.acceptedQuantity"),
          value: formatQty(record.acceptedQuantity),
        },
        {
          label: t("inventory.purchasePricePerUnit"),
          value: formatMoney(record.purchasePricePerUnit),
        },
        {
          label: t("inventory.poNumber"),
          value: record.poNumber || "—",
        },
        {
          label: t("inventory.invoiceNumber"),
          value: record.vendorInvoiceNumber || "—",
        },
        {
          label: t("dateTime.invoiceDate"),
          value: formatDate(record.invoiceDate, t),
        },
        {
          label: t("inventory.warrantyStart"),
          value: formatDate(record.warrantyStartDate, t),
        },
        {
          label: t("inventory.warrantyEnd"),
          value: formatDate(record.warrantyEndDate, t),
        },
        {
          label: t("inventory.projectName"),
          value: record.projectName || "—",
        },
        {
          label: t("inventory.iofNumber"),
          value: record.iofNumber || "—",
        },
        {
          label: t("common.remarks"),
          value: record.remarks || "—",
        },
      ]
    : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="bg-[#0a1230]/40 backdrop-blur-xs fixed inset-0 z-40 transition-opacity animate-in fade-in duration-200" />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%]",
            "w-[95vw] sm:w-[90vw] md:w-[520px] max-h-[85vh]",
            "bg-white rounded-2xl shadow-[0_30px_80px_-20px_rgba(30,51,131,0.45)] border border-white/60",
            "p-6 flex flex-col gap-4 font-sans focus:outline-hidden overflow-hidden",
            "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          )}
        >
          <div className="flex items-center justify-between shrink-0">
            <DialogTitle className="text-[16px] font-bold text-foreground">
              {t("inventory.inventoryRecordDetail")}
            </DialogTitle>
            <DialogPrimitive.Close className="h-8 w-8 grid place-items-center rounded-lg per-file-hint hover:bg-workspace/60 hover:text-primary transition focus:outline-hidden">
              <X className="h-4 w-4" />
              <span className="sr-only">{t("formActions.close")}</span>
            </DialogPrimitive.Close>
          </div>

          <div className="overflow-y-auto min-h-0 pr-1 -mr-1">
            {loading ? (
              <div className="space-y-3 animate-pulse py-2">
                {[1, 2, 3, 4, 5, 6].map((idx) => (
                  <div key={idx} className="h-8 bg-gray-50 rounded-lg w-full" />
                ))}
              </div>
            ) : error ? (
              <div className="py-8 text-center text-error font-semibold text-sm">
                ⚠️ {error}
              </div>
            ) : (
              <dl className="divide-y divide-(--color-text-border)/30">
                {rows.map((row) => (
                  <div
                    key={row.label}
                    className="flex items-start justify-between gap-4 py-2.5 text-xs"
                  >
                    <dt className="per-file-hint shrink-0 font-semibold">
                      {row.label}
                    </dt>
                    <dd className="font-semibold text-foreground text-right break-words max-w-[60%]">
                      {row.value}
                    </dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
